import React, { useEffect, useState, useRef } from 'react';
import { Search, Bell, ChevronDown, TrendingUp, Map, BarChart3, Zap, Wind, Sun, Clock, Leaf, TrendingDown, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface TopBarProps {
  activeRegion: string;
  setActiveRegion: (region: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeView?: string;
  setActiveView?: (view: string) => void;
  notifications?: any[];
  hasUnreadNotifications?: boolean;
  onMarkAllAsRead?: () => void;
  onMarkAsRead?: (id: string) => void;
  readNotifications?: Set<string>;
  availableRegions?: string[];
  onNewChat?: () => void; // New prop for clearing chat
}
// Search result types
type SearchResultType = 'view' | 'model' | 'region' | 'data';
interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  description: string;
  icon: any;
  action: () => void;
}
export function TopBar({
  activeRegion,
  setActiveRegion,
  searchQuery,
  setSearchQuery,
  activeView,
  setActiveView,
  notifications = [],
  hasUnreadNotifications = false,
  onMarkAllAsRead,
  onMarkAsRead,
  readNotifications = new Set(),
  availableRegions = ['Germany', 'Spain', 'UK', 'France'],
  onNewChat
}: TopBarProps) {
  // Only show region selector on overview page
  const showRegionSelector = activeView === 'overview';
  // Debug logging
  console.log('TopBar Debug:', {
    activeView,
    showRegionSelector,
    availableRegions,
    availableRegionsLength: availableRegions?.length
  });
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [localSearchQuery, setLocalSearchQuery] = useState(''); // Use local state for search
  // Mock search results - now with REAL navigation actions
  const allResults: SearchResult[] = [{
    id: 'overview-view',
    type: 'view',
    title: 'Overview',
    description: 'Dashboard overview and metrics',
    icon: BarChart3,
    action: () => setActiveView?.('overview')
  }, {
    id: 'forecast-view',
    type: 'view',
    title: 'Forecast',
    description: 'View detailed weather forecasts',
    icon: TrendingUp,
    action: () => setActiveView?.('forecast')
  }, {
    id: 'benchmarks-view',
    type: 'view',
    title: 'Benchmarks',
    description: 'Compare model performance',
    icon: BarChart3,
    action: () => setActiveView?.('benchmarks')
  }, {
    id: 'oracle-view',
    type: 'view',
    title: 'Oracle',
    description: 'AI-powered insights',
    icon: Zap,
    action: () => setActiveView?.('oracle')
  }, {
    id: 'market-view',
    type: 'view',
    title: 'Market Aggregates',
    description: 'Market data and analytics',
    icon: BarChart3,
    action: () => setActiveView?.('market-aggregates')
  }, {
    id: 'ept2-model',
    type: 'model',
    title: 'EPT-2 Model',
    description: 'Jua proprietary forecast model',
    icon: Zap,
    action: () => {
      setActiveView?.('benchmarks');
      // Could add model filtering here
    }
  }, {
    id: 'gfs-model',
    type: 'model',
    title: 'GFS Model',
    description: 'Global Forecast System',
    icon: TrendingUp,
    action: () => {
      setActiveView?.('benchmarks');
      // Could add model filtering here
    }
  }, {
    id: 'germany-region',
    type: 'region',
    title: 'Germany',
    description: 'Switch to Germany region',
    icon: Map,
    action: () => {
      setActiveRegion('Germany');
      if (activeView === 'oracle' || activeView === 'market-aggregates') {
        setActiveView?.('overview');
      }
    }
  }, {
    id: 'spain-region',
    type: 'region',
    title: 'Spain',
    description: 'Switch to Spain region',
    icon: Map,
    action: () => {
      setActiveRegion('Spain');
      if (activeView === 'oracle' || activeView === 'market-aggregates') {
        setActiveView?.('overview');
      }
    }
  }, {
    id: 'uk-region',
    type: 'region',
    title: 'United Kingdom',
    description: 'Switch to UK region',
    icon: Map,
    action: () => {
      setActiveRegion('UK');
      if (activeView === 'oracle' || activeView === 'market-aggregates') {
        setActiveView?.('overview');
      }
    }
  }, {
    id: 'france-region',
    type: 'region',
    title: 'France',
    description: 'Switch to France region',
    icon: Map,
    action: () => {
      setActiveRegion('France');
      if (activeView === 'oracle' || activeView === 'market-aggregates') {
        setActiveView?.('overview');
      }
    }
  }, {
    id: 'wind-data',
    type: 'data',
    title: 'Wind Patterns',
    description: 'View wind generation data',
    icon: Wind,
    action: () => setActiveView?.('forecast')
  }, {
    id: 'solar-data',
    type: 'data',
    title: 'Solar Irradiance',
    description: 'View solar generation data',
    icon: Sun,
    action: () => setActiveView?.('forecast')
  }];
  // Filter results based on LOCAL search query
  const filteredResults = localSearchQuery.trim() ? allResults.filter(result => result.title.toLowerCase().includes(localSearchQuery.toLowerCase()) || result.description.toLowerCase().includes(localSearchQuery.toLowerCase())) : allResults.slice(0, 6); // Show top 6 when empty
  // Group results by type
  const groupedResults = filteredResults.reduce((acc, result) => {
    if (!acc[result.type]) acc[result.type] = [];
    acc[result.type].push(result);
    return acc;
  }, {} as Record<SearchResultType, SearchResult[]>);
  const typeLabels: Record<SearchResultType, string> = {
    view: 'Views',
    model: 'Models',
    region: 'Regions',
    data: 'Data Types'
  };
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        setIsSearchOpen(true);
      }
      // Escape to close search
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
        searchInputRef.current?.blur();
      }
      // Arrow navigation when dropdown is open
      if (isSearchOpen && filteredResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % filteredResults.length);
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + filteredResults.length) % filteredResults.length);
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          filteredResults[selectedIndex]?.action();
          setIsSearchOpen(false);
          setLocalSearchQuery('');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, filteredResults, selectedIndex]);
  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) && !searchInputRef.current?.contains(e.target as Node)) {
        setIsSearchOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node) && !notificationButtonRef.current?.contains(e.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [localSearchQuery]);
  const handleResultClick = (result: SearchResult) => {
    result.action();
    setIsSearchOpen(false);
    setLocalSearchQuery('');
  };
  return <header className="h-16 bg-white/60 backdrop-blur-xl border-b border-slate-50 flex items-center justify-between px-6 relative z-20 shadow-sm shadow-slate-200/40" data-id="element-614">
      {/* Left: Search */}
      <div className="flex items-center gap-4 flex-1 max-w-md relative" data-id="element-615">
        <div className="relative flex-1" data-id="element-616">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" data-id="element-617" />
          <input ref={searchInputRef} type="text" placeholder="Search forecasts, models..." value={localSearchQuery} onChange={e => setLocalSearchQuery(e.target.value)} onFocus={() => setIsSearchOpen(true)} className="w-full pl-9 pr-16 py-2 bg-white/80 border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500/50 transition-all" data-id="element-618" />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-0.5 text-[10px] text-slate-400 font-medium z-10" data-id="element-619">
            <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-slate-600" data-id="element-620">
              ⌘
            </kbd>
            <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-slate-600" data-id="element-621">
              K
            </kbd>
          </div>

          {/* Search Dropdown */}
          <AnimatePresence data-id="element-622">
            {isSearchOpen && <motion.div ref={dropdownRef} initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl shadow-2xl shadow-slate-900/10 overflow-hidden max-h-[500px] overflow-y-auto custom-scrollbar" data-id="element-623">
                {filteredResults.length > 0 ? <div className="py-2" data-id="element-624">
                    {Object.entries(groupedResults).map(([type, results]) => <div key={type} className="mb-3 last:mb-0" data-id="element-625">
                        <div className="px-3 py-1.5" data-id="element-626">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-627">
                            {typeLabels[type as SearchResultType]}
                          </p>
                        </div>
                        {results.map((result, idx) => {
                  const globalIndex = filteredResults.indexOf(result);
                  const isSelected = globalIndex === selectedIndex;
                  return <button key={result.id} onClick={() => handleResultClick(result)} className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors ${isSelected ? 'bg-pink-50 border-l-2 border-pink-500' : 'hover:bg-slate-50 border-l-2 border-transparent'}`} data-id="element-628">
                              <div className={`p-2 rounded-lg ${isSelected ? 'bg-pink-100' : 'bg-slate-100'}`} data-id="element-629">
                                <result.icon size={16} className={isSelected ? 'text-pink-600' : 'text-slate-600'} data-id="element-630" />
                              </div>
                              <div className="flex-1 text-left" data-id="element-631">
                                <p className={`text-sm font-medium ${isSelected ? 'text-pink-900' : 'text-slate-900'}`} data-id="element-632">
                                  {result.title}
                                </p>
                                <p className="text-xs text-slate-500" data-id="element-633">
                                  {result.description}
                                </p>
                              </div>
                              {isSelected && <kbd className="px-2 py-1 bg-slate-100 border border-slate-200 rounded text-[10px] text-slate-600 font-medium" data-id="element-634">
                                  ↵
                                </kbd>}
                            </button>;
                })}
                      </div>)}
                  </div> : <div className="py-12 text-center" data-id="element-635">
                    <Search size={32} className="mx-auto text-slate-300 mb-3" data-id="element-636" />
                    <p className="text-sm font-medium text-slate-900 mb-1" data-id="element-637">
                      No results found
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-638">
                      Try searching for views, models, or regions
                    </p>
                  </div>}

                {/* Footer hint */}
                <div className="border-t border-slate-200 px-3 py-2 bg-slate-50/50" data-id="element-639">
                  <div className="flex items-center justify-between text-[10px] text-slate-500" data-id="element-640">
                    <div className="flex items-center gap-3" data-id="element-641">
                      <span className="flex items-center gap-1" data-id="element-642">
                        <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded" data-id="element-643">
                          ↑
                        </kbd>
                        <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded" data-id="element-644">
                          ↓
                        </kbd>
                        navigate
                      </span>
                      <span className="flex items-center gap-1" data-id="element-645">
                        <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded" data-id="element-646">
                          ↵
                        </kbd>
                        select
                      </span>
                      <span className="flex items-center gap-1" data-id="element-647">
                        <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded" data-id="element-648">
                          esc
                        </kbd>
                        close
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Center: Region Tabs (hidden on market-aggregates and oracle views) */}
      {showRegionSelector && <div className="flex items-center gap-1 bg-slate-50/80 p-1 rounded-lg border border-slate-200/60 mx-4 flex-shrink-0" data-id="element-649">
          {availableRegions.map(region => {
        const isActive = activeRegion === region;
        return <motion.button key={region} onClick={() => setActiveRegion(region)} className={`relative px-4 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} whileTap={{
          scale: 0.95
        }} data-id="element-650">
                {isActive && <motion.div layoutId="activeTab" className="absolute inset-0 bg-white rounded-md shadow-sm" transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.6
          }} data-id="element-651" />}
                <span className="relative z-10" data-id="element-652">{region}</span>
              </motion.button>;
      })}
        </div>}

      {/* Right: Actions */}
      <div className="flex items-center gap-3 flex-1 justify-end" data-id="element-653">
        {/* New Chat Button - Only visible on Oracle view */}
        {activeView === 'oracle' && onNewChat && <button onClick={onNewChat} className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-lg transition-all shadow-sm hover:shadow-md text-sm font-medium" title="Start a new chat conversation" data-id="element-654">
            <Plus size={16} data-id="element-655" />
            <span className="hidden sm:inline" data-id="element-656">New Chat</span>
          </button>}

        <div className="relative" data-id="element-657">
          <button ref={notificationButtonRef} onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} className="p-2 hover:bg-slate-100/80 rounded-lg transition-colors text-slate-600 hover:text-slate-900 relative" data-id="element-658">
            <Bell size={18} data-id="element-659" />
            {hasUnreadNotifications && <span className="absolute top-1 right-1 w-2 h-2 bg-[#ff006b] rounded-full border-2 border-white" data-id="element-660" />}
          </button>

          {/* Notifications Dropdown */}
          <AnimatePresence data-id="element-661">
            {isNotificationsOpen && <motion.div ref={notificationsRef} initial={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="absolute right-0 top-full mt-2 w-96 bg-white border border-slate-200 rounded-2xl shadow-2xl shadow-slate-900/10 overflow-hidden flex flex-col" data-id="element-662">
                <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10" data-id="element-663">
                  <div className="flex items-center justify-between mb-1" data-id="element-664">
                    <h3 className="text-sm font-semibold text-slate-900" data-id="element-665">
                      Notifications
                    </h3>
                    {hasUnreadNotifications && <button onClick={e => {
                  e.stopPropagation();
                  onMarkAllAsRead?.();
                }} className="text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-666">
                        Mark all read
                      </button>}
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-667">
                    Regional alerts and market updates
                  </p>
                </div>

                <div className="overflow-y-auto max-h-[400px] notifications-scrollbar" data-id="element-668">
                  {notifications.length > 0 ? notifications.map((notif, idx) => {
                const iconMap = {
                  wind: Wind,
                  zap: Zap,
                  leaf: Leaf
                };
                const Icon = iconMap[notif.icon as keyof typeof iconMap] || Zap;
                const iconColorClasses = {
                  emerald: 'text-emerald-600 bg-emerald-50',
                  cyan: 'text-cyan-600 bg-cyan-50',
                  amber: 'text-amber-600 bg-amber-50',
                  blue: 'text-blue-600 bg-blue-50'
                };
                const iconColors = iconColorClasses[notif.color as keyof typeof iconColorClasses];
                const isUnread = !readNotifications.has(notif.id);
                const getTargetView = () => {
                  if (notif.type === 'wind' || notif.type === 'renewable') {
                    return 'forecast';
                  }
                  if (notif.type === 'price' || notif.type === 'load') {
                    return 'overview';
                  }
                  return 'overview';
                };
                // Calculate time ago (mock - in real app, use actual timestamp)
                const timeAgo = idx === 0 ? '21 min ago' : idx === 1 ? '1 hour ago' : idx === 2 ? '3 hours ago' : '1 day ago';
                return <button key={notif.id} onClick={() => {
                  onMarkAsRead?.(notif.id);
                  setActiveRegion(notif.region);
                  setActiveView?.(getTargetView());
                  setIsNotificationsOpen(false);
                }} className={`w-full p-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-b-0 text-left group relative ${isUnread ? 'bg-slate-25' : ''}`} data-id="element-669">
                          {isUnread && <div className="absolute top-3 right-3" data-id="element-670">
                              <span className="inline-flex h-2 w-2 rounded-full bg-pink-500" data-id="element-671"></span>
                            </div>}

                          <div className="flex items-start gap-3" data-id="element-672">
                            <div className={`p-2 rounded-lg ${iconColors} flex-shrink-0`} data-id="element-673">
                              <Icon size={14} data-id="element-674" />
                            </div>

                            <div className="flex-1 min-w-0 pr-6" data-id="element-675">
                              <div className="flex items-center gap-2 mb-1" data-id="element-676">
                                <span className="text-[10px] font-medium text-slate-900 uppercase tracking-wider" data-id="element-677">
                                  {notif.regionCode}
                                </span>
                                <span className="text-xs text-slate-300" data-id="element-678">
                                  •
                                </span>
                                <span className="text-[10px] text-slate-500 capitalize" data-id="element-679">
                                  {notif.type}
                                </span>
                                <span className="text-xs text-slate-300" data-id="element-680">
                                  •
                                </span>
                                <span className="text-[10px] text-slate-400" data-id="element-681">
                                  {timeAgo}
                                </span>
                              </div>
                              <p className="text-sm text-slate-900 mb-1.5 group-hover:text-slate-700 transition-colors" data-id="element-682">
                                {notif.title}
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-683">
                                <span className="text-sm font-medium text-slate-900" data-id="element-684">
                                  {notif.value}
                                </span>
                                <span className="text-sm text-slate-500" data-id="element-685">
                                  {notif.subtitle}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>;
              }) : <div className="py-12 text-center" data-id="element-686">
                      <div className="inline-flex p-4 rounded-2xl bg-slate-100/50 backdrop-blur-sm mb-3" data-id="element-687">
                        <Bell size={32} className="text-slate-400" data-id="element-688" />
                      </div>
                      <p className="text-sm font-medium text-slate-900 mb-1" data-id="element-689">
                        No alerts
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-690">
                        All systems normal
                      </p>
                    </div>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* User Menu - Single Instance */}
        <div className="relative" onMouseEnter={() => setIsUserMenuOpen(true)} onMouseLeave={() => setIsUserMenuOpen(false)} data-id="element-691">
          <button className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100/80 rounded-lg transition-colors" data-id="element-692">
            <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-[#ff006b] to-[#a855f7] flex items-center justify-center text-white text-xs font-bold" data-id="element-693">
              M
            </div>
            <ChevronDown size={14} className={`text-slate-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} data-id="element-694" />
          </button>

          <AnimatePresence data-id="element-695">
            {isUserMenuOpen && <motion.div initial={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="absolute right-0 top-full mt-2 w-64 bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl shadow-2xl shadow-slate-900/10 overflow-hidden" data-id="element-696">
                <div className="p-4 border-b border-slate-200 bg-gradient-to-br from-pink-50/50 to-purple-50/50" data-id="element-697">
                  <div className="flex items-center gap-3" data-id="element-698">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#ff006b] to-[#a855f7] flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-pink-500/30" data-id="element-699">
                      M
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-700">
                      <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-701">
                        Mathias Van der Brempt
                      </p>
                      <p className="text-xs text-slate-500 truncate" data-id="element-702">
                        mathias@jua.ai
                      </p>
                    </div>
                  </div>
                </div>

                <div className="py-2" data-id="element-703">
                  <button className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3" data-id="element-704">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-705">
                      <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-706">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" data-id="element-707" />
                      </svg>
                    </div>
                    <span className="font-medium" data-id="element-708">Profile Settings</span>
                  </button>

                  <button className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3" data-id="element-709">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-710">
                      <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-711">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" data-id="element-712" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" data-id="element-713" />
                      </svg>
                    </div>
                    <span className="font-medium" data-id="element-714">Preferences</span>
                  </button>

                  <button className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3" data-id="element-715">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-716">
                      <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-717">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" data-id="element-718" />
                      </svg>
                    </div>
                    <span className="font-medium" data-id="element-719">API Settings</span>
                  </button>
                </div>

                <div className="border-t border-slate-200" data-id="element-720" />

                <div className="py-2" data-id="element-721">
                  <button className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3" data-id="element-722">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-723">
                      <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-724">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-725" />
                      </svg>
                    </div>
                    <span className="font-medium" data-id="element-726">Help & Support</span>
                  </button>

                  <button className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3" data-id="element-727">
                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center" data-id="element-728">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-729">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" data-id="element-730" />
                      </svg>
                    </div>
                    <span className="font-medium" data-id="element-731">Sign Out</span>
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </header>;
}